import { createActionCreator, createReducer } from 'deox';
import { Draft, produce } from 'immer';
import { isNil, last } from 'ramda';
import { createSelector } from 'reselect';
import { nanoid } from 'nanoid/non-secure';
import { AppState, PaidFeatures, ToolName } from 'types/AppState';
import {
  ScalingType,
  Scene,
  SceneItem,
  Scenes,
  SceneType,
  TempAudio,
  TempScenes
} from 'types/Scene';
import { Transition, Transitions, TransitionType } from 'types/Transitions';
import { DEFAULT_IMAGE_DURATION, EMPTY_TRANSITION } from 'constants/scenes';
import { getEffects, getSceneIdsWithPaidFonts, isFontPaid } from './effects';
import { getAudio, isAudioPaid } from './audio';
import { Licenses } from 'types/Licenses';
import { Effects } from 'types/Effects';
import { RatioFormatType } from 'types/RatioFormat';
import { findTransitionById } from 'utils/transitions';
import { noop } from 'utils/noop';
import { ScenesLoader } from 'modules/scenesLoader';
import { getCurrentTemplateId, isTemplateUsed } from 'modules/template';
import { Audio } from 'types/Audio';
import { ToolDetails, ToolDetailsName } from 'utils/analytics';
import { PremiumItemSource } from '../constants';
import { Player } from 'utils/player/Player';

type SceneData = Omit<Scene, 'start'>;

export const createDefaultScene = (id: string): SceneItem => ({
  type: SceneType.EMPTY,
  duration: 0,
  maxDuration: 0,
  trimStart: 0,
  transition: EMPTY_TRANSITION,
  scalingMode: ScalingType.FIT,
  id,
  size: { width: 0, height: 0 },
  sourceId: ''
});

const defaultId = nanoid();
export const defaultScenesState: Scenes = {
  current: defaultId,
  items: [createDefaultScene(defaultId)]
};

export const createScene = createActionCreator(
  'SCENE/CREATE',
  resolve => (id: string, undoable?: boolean) => resolve({ id, undoable })
);
export const createStoreScene = createActionCreator(
  'SCENE/CREATE_IN_STORE',
  resolve => (id: string) => resolve(id)
);
export const updateScene = createActionCreator(
  'SCENE/UPDATE',
  resolve => (data: Partial<SceneItem>, id?: string) => resolve({ id, data })
);
export const addSceneWithData = createActionCreator(
  'SCENE/ADD_SCENE',
  resolve => (scene: SceneItem) => resolve(scene)
);
export const deleteStoreScene = createActionCreator(
  'SCENE/DELETE_FROM_STORE',
  resolve => (id: string) => resolve(id)
);
export const deleteNotUploadedScenes = createActionCreator('SCENE/DELETE_NOT_UPLOADED_SCENES');
export const setCurrentScene = createActionCreator('SCENE/SET_CURRENT', resolve => (id: string) =>
  resolve(id)
);
export const uploadFiles = createActionCreator('SCENE/UPLOAD_FILES', resolve => (files: File[]) =>
  resolve(files)
);
export const uploadLogFile = createActionCreator(
  'SCENE/UPLOAD_LOG_FILE',
  resolve => (tempScenes: TempScenes, tempAudio: TempAudio) => resolve({ tempScenes, tempAudio })
);
export const setCurrentSceneByTimeline = createActionCreator('SCENE/SET_CURRENT_BY_TIMELINE');
export const uploadSceneContent = createActionCreator(
  'SCENE/UPLOAD_CONTENT',
  resolve => (file: File, id?: string) => resolve({ file, id })
);
export const setVideoContentFromTool = createActionCreator(
  'SCENE/SET_VIDEO_CONTENT_FROM_TOOL',
  resolve => (
    url: string,
    bigResSrc: string,
    license: Licenses,
    sceneId: string,
    videoId: string,
    source: PremiumItemSource
  ) => resolve({ url, bigResSrc, license, sceneId, videoId, source })
);
export const moveScene = createActionCreator(
  'SCENE/MOVE',
  resolve => (start: number, end: number) => resolve({ start, end })
);
export const moveStoreScene = createActionCreator(
  'SCENE/MOVE_IN_STORE',
  resolve => (end: number, start: number) => resolve({ start, end })
);
export const trimScene = createActionCreator(
  'SCENE/TRIM',
  resolve => (id: string, duration: number, start: number) => resolve({ id, duration, start })
);
export const selectScene = createActionCreator(
  'SCENE/SELECT',
  resolve => (id: string, shouldSeek: boolean = true) => resolve({ id, shouldSeek })
);
export const setSceneTransition = createActionCreator(
  'SCENE/SET_TRANSITION',
  resolve => (transitionId: Transition['id'], sceneId: SceneItem['id'], undoable?: boolean) =>
    resolve({ sceneId, transitionId, undoable })
);
export const addPhotoSidebarImageToScene = createActionCreator(
  'SCENE/ADD_PHOTO_SIDEBAR_IMAGE',
  resolve => (src: string, photo, id: string, loaded: () => void = noop) =>
    resolve({ src, photo, id, loaded })
);
export const deleteAllScenes = createActionCreator('SCENE/DELETE_ALL');
export const loadActualShutterstockUrls = createActionCreator(
  'SCENE/LOAD_ACTUAL_SHUTTERSTOCK_URLS'
);
export const makeScenesWithoutSrcEmpty = createActionCreator('SCENE/MAKE_SCENES_WITHOUT_SRC_EMPTY');
export const clearSrcForShutterstockImages = createActionCreator(
  'SCENE/CLEAR_SRC_SHUTTERSTOCK_IMAGES'
);
export const applyTransitionToAllScene = createActionCreator(
  'SCENE/SET_TRANSITION_TO_ALL_SCENE',
  resolve => (transitionId: Transition['id']) => resolve(transitionId)
);

export const scenes = createReducer(defaultScenesState, handle => [
  handle(addSceneWithData, (state, scene) => ({
    ...state,
    items: [...state.items, scene.payload]
  })),
  handle(
    createStoreScene,
    produce<(d: Draft<Scenes>, a: ReturnType<typeof createStoreScene>) => void>(
      (draft, { payload: id }) => {
        const newScene = createDefaultScene(id);
        draft.items.push(newScene);
        draft.current = newScene.id;
      }
    )
  ),
  handle(
    updateScene,
    produce<(d: Draft<Scenes>, a: ReturnType<typeof updateScene>) => void>(
      (draft, { payload: { id, data } }) => {
        const sceneId = id ?? draft.current;
        const scene = draft.items.find(item => sceneId === item.id);
        if (scene) Object.assign(scene, data);
      }
    )
  ),
  handle(
    deleteStoreScene,
    produce<(d: Draft<Scenes>, a: ReturnType<typeof deleteStoreScene>) => void>(
      (draft, { payload: id }) => {
        const indx = draft.items.findIndex(item => item.id === id);
        draft.items = draft.items.filter(item => item.id !== id);
        const newid = nanoid();
        if (draft.items.length === 0) {
          draft.items.push(createDefaultScene(newid));
          draft.current = newid;
        } else if (draft.current === id) {
          draft.current = draft.items[indx - 1] ? draft.items[indx - 1].id : draft.items[indx].id;
        }

        // remove transition after last scene
        draft.items[draft.items.length - 1] = {
          ...draft.items[draft.items.length - 1],
          transition: EMPTY_TRANSITION
        };
      }
    )
  ),
  handle(
    setCurrentScene,
    produce<(d: Draft<Scenes>, a: ReturnType<typeof setCurrentScene>) => void>(
      (draft, { payload: id }) => {
        draft.current = id;
      }
    )
  ),
  handle(
    moveStoreScene,
    produce<(d: Draft<Scenes>, a: ReturnType<typeof moveStoreScene>) => void>(
      (draft, { payload: { start, end } }) => {
        draft.items[start].transition = EMPTY_TRANSITION;
        draft.items.splice(end, 0, ...draft.items.splice(start, 1));
      }
    )
  ),
  handle(
    deleteAllScenes,
    produce<(d: Draft<Scenes>, a: ReturnType<typeof deleteAllScenes>) => void>(() => ({
      ...defaultScenesState
    }))
  ),
  handle(
    makeScenesWithoutSrcEmpty,
    produce<(d: Draft<Scenes>, a: ReturnType<typeof makeScenesWithoutSrcEmpty>) => void>(draft => {
      draft.items.forEach((item: Scene) => (item.type = item.src ? item.type : SceneType.EMPTY));
    })
  ),
  handle(
    clearSrcForShutterstockImages,
    produce<(d: Draft<Scenes>, a: ReturnType<typeof clearSrcForShutterstockImages>) => void>(
      draft => {
        draft.items.forEach((item: Scene) => (item.src === item.sourceId ? null : item.src));
      }
    )
  )
]);

export const computeScenesTimeChain = (
  items: SceneData[],
  transitions: Transitions['list'] = [],
  sceneSources: ScenesLoader
) =>
  items
    .filter(item => !!item)
    .reduce((acc, curr, i) => {
      const prev = acc[i - 1];
      const transition = {
        ...findTransitionById(transitions, curr.transition?.id),
        duration: curr.transition?.duration ?? 0
      };

      if (!isNil(prev)) {
        const isStartAffected =
          prev.transition?.type === TransitionType.WipeDiagonal ||
          prev.transition?.type === TransitionType.Fade;

        acc[i] = {
          ...curr,
          start:
            prev.start + prev.duration - (isStartAffected ? prev.transition?.duration ?? 0 : 0),
          prevTransition: prev.transition,
          transition,
          localSrc: sceneSources?.sources?.[curr.id]
        };
      } else {
        acc[i] = {
          ...curr,
          transition,
          localSrc: sceneSources?.sources?.[curr.id],
          start: 0
        };
      }

      return acc;
    }, new Array<Scene>(items.length));

export const getCurrentSceneId = (state: AppState): Scene['id'] => state.scenes.current;

export const getScenes = createSelector<
  AppState,
  SceneItem[],
  Transitions['list'],
  ScenesLoader,
  Scene[]
>(
  state => state.scenes.items,
  state => state.transitions?.list,
  state => state.scenesSources,
  computeScenesTimeChain
);

export const getScene = (id: Scene['id']) => (state: AppState): Scene | null =>
  getScenes(state).find(item => item.id === id);

export const getCurrentScene = (state: AppState): Scene | null => {
  const id = getCurrentSceneId(state);
  return getScene(id)(state);
};

export const getDuration = (state: AppState): number => {
  const { start, duration } = last(getScenes(state));
  return start + duration;
};

export const isPremiumContentUsed = (state: AppState): boolean => {
  return state.scenes.items.some(
    scene =>
      scene.license &&
      scene.license === Licenses.PhotoChooserPremium &&
      !state.oneTimePayment.userPaidItems.find(item => item.id === scene.sourceId)
  );
};

export const isShutterstockVideoUsed = (state: AppState): boolean => {
  return state.scenes.items.some(
    scene =>
      scene.license &&
      scene.license === Licenses.ShutterstockVideo &&
      !state.oneTimePayment.userPaidItems.find(item => item.id === scene.sourceId)
  );
};

export const isTextPresetsUsed = (state: AppState): boolean => {
  return Object.values(state.effects)
    .map(effect => Object.values(effect).some(value => value.presetId))
    .some(value => value === true);
};

export const getScenesWithPremiumContentUsed = createSelector<AppState, Scene[], Scene['id'][]>(
  getScenes,
  sceneItems =>
    sceneItems
      .filter(
        scene =>
          scene.license &&
          (scene.license === Licenses.PhotoChooserPremium ||
            scene.license === Licenses.TemplatePremium ||
            scene.license === Licenses.StockVideoPremium ||
            scene.license === Licenses.ShutterstockVideo)
      )
      .map(scene => scene.id)
);

export const getUsedTools = createSelector<
  AppState,
  Scene[],
  Effects,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  ToolName[]
>(
  getScenes,
  getEffects,
  isTextPresetsUsed,
  state => !isNil(state.audio),
  state => state.ratioFormat !== RatioFormatType.Youtube,
  state => state.scenes.items.some(scene => scene.license === Licenses.TemplatePremium),
  state =>
    state.scenes.items.some(
      scene =>
        scene.license === Licenses.StockVideoFree || scene.license === Licenses.StockVideoPremium
    ),
  state => state.scenes.items.some(scene => scene.license === Licenses.ShutterstockVideo),
  (
    sceneItems,
    effects,
    isTextPresetUsed,
    isAudioUsed,
    isFormatChanged,
    isTemplateUsedLocal,
    isStockVideosUsed
  ) => {
    const result: ToolName[] = [];

    if (Object.values(effects).some(item => item.length > 0)) {
      result.push(ToolName.Text);
    }

    if (isTextPresetUsed) {
      result.push(ToolName.TextPreset);
    }

    if (
      sceneItems.some(
        item =>
          item.license === Licenses.PhotoChooserPremium ||
          item.license === Licenses.PhotoChooserFree
      )
    ) {
      result.push(ToolName.Photo);
    }

    if (
      sceneItems.some(
        item =>
          (item.type === SceneType.VIDEO && item.duration !== item.maxDuration) ||
          (item.type === SceneType.IMAGE && item.duration !== DEFAULT_IMAGE_DURATION)
      )
    ) {
      result.push(ToolName.Trim);
    }

    if (sceneItems.some(item => item.transition?.type !== TransitionType.None)) {
      result.push(ToolName.Transition);
    }

    if (isAudioUsed) {
      result.push(ToolName.Music);
    }

    if (isFormatChanged) {
      result.push(ToolName.Fit);
    }

    if (isTemplateUsedLocal) {
      result.push(ToolName.Template);
    }

    if (isStockVideosUsed) {
      result.push(ToolName.StockVideos);
    }

    return result;
  }
);

export const isPaidTransitionUsed = createSelector<AppState, Scene[], boolean>(getScenes, items =>
  items.some(item => item.transition.is_paid)
);

export const getToolsDetails = createSelector<
  AppState,
  Effects,
  Audio,
  Scene[],
  Boolean,
  string,
  ToolDetails[]
>(
  (state: AppState) => getEffects(state),
  (state: AppState) => getAudio(state),
  (state: AppState) => getScenes(state),
  (state: AppState) => isTemplateUsed(state),
  (state: AppState) => getCurrentTemplateId(state),
  (effects, audio, scenes, isTemplateUsed, templateId) => {
    const toolDetails: ToolDetails[] = [];
    if (!isNil(audio)) {
      toolDetails.push({
        name: ToolDetailsName,
        id: audio.id,
        is_premium: audio.isPaid,
        item: 'music_tool'
      });
    }

    Object.values(effects)
      .flat()
      .forEach(ef =>
        toolDetails.push({
          name: ToolDetailsName,
          id: ef.id,
          is_premium: ef.font.isPaid,
          item: 'text'
        })
      );

    scenes.forEach(sc =>
      toolDetails.push({
        name: ToolDetailsName,
        id: sc.id,
        is_premium:
          !!sc.license &&
          sc.license !== Licenses.StockVideoFree &&
          sc.license !== Licenses.PhotoChooserFree,
        item: sc.type === SceneType.IMAGE ? 'photo' : 'video',
        is_shutterstock: sc.source === PremiumItemSource.Shutterstock
      })
    );

    if (isTemplateUsed) {
      toolDetails.push({
        name: ToolDetailsName,
        id: templateId,
        is_premium: true,
        item: 'template',
        is_shutterstock: false
      });
    }
    return toolDetails;
  }
);

export const getUsedPaidFeatures = createSelector<
  AppState,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  boolean,
  PaidFeatures[]
>(
  (state: AppState) => isTemplateUsed(state),
  (state: AppState) => isFontPaid(state),
  (state: AppState) => isAudioPaid(state),
  (state: AppState) => isPremiumContentUsed(state),
  (state: AppState) => isPaidTransitionUsed(state),
  (state: AppState) => isTextPresetsUsed(state),
  (state: AppState) => state.scenes.items.some(item => item.license === Licenses.StockVideoPremium),
  (state: AppState) => isShutterstockVideoUsed(state),
  (
    templatePaid,
    fontPaid,
    audioPaid,
    premiumContentPaid,
    transitionsPaid,
    textPresetUsed,
    paidStockVideos,
    shutterstockVideos
  ) => {
    const paidFeatures: [PaidFeatures, boolean][] = [
      [PaidFeatures.Template, templatePaid],
      [PaidFeatures.Font, fontPaid],
      [PaidFeatures.Audio, audioPaid],
      [PaidFeatures.PhotoChooser, premiumContentPaid],
      [PaidFeatures.Transition, transitionsPaid],
      [PaidFeatures.TextPreset, textPresetUsed],
      [PaidFeatures.VideoChooser, paidStockVideos],
      [PaidFeatures.ShutterstockVideo, shutterstockVideos]
    ];
    return paidFeatures.filter(item => !!item[1]).map(item => item[0]);
  }
);

export const getSceneIdsWithPaidFeatures = createSelector<AppState, string[], string[], string[]>(
  (state: AppState) => getScenesWithPremiumContentUsed(state),
  (state: AppState) => getSceneIdsWithPaidFonts(state),
  (premium, fonts) => [...premium, ...fonts]
);

export const getProjectScenes = (state: AppState): SceneItem[] => state.scenes.items;

// current scene according to currentTime in timeline
export const getSceneByTimeline = (player: Player) => (state: AppState, items?: Scene[]): Scene => {
  const s = items ?? getScenes(state);

  const len = s.length;
  for (let i = 0; i < len; i++) {
    const curScene = s[i];
    const prevScene = s[i - 1];

    // in case it is a "stub" scene from template
    if (!curScene) return;

    if (curScene.start > player.time) return prevScene;
  }

  return last(s);
};

export const getSceneByTimelineAndScenes = (player: Player) => (items: Scene[]) => (
  state: AppState
) => getSceneByTimeline(player)(state, items);

export const getSceneIdByTimeline = (player: Player) => (state: AppState) =>
  getSceneByTimeline(player)(state)?.id;
